<template>
  <v-container fluid>
    <!-- Header -->
    <v-row class="mb-4" align="center">
      <v-col>
        <h2 class="text-primary text-h5 font-weight-medium">
          Contextes Codes Erreur ({{ totalContexts }})
        </h2>
      </v-col>

      <v-col cols="auto">
        <v-btn color="primary" prepend-icon="mdi-plus" @click="createContext">
          Nouveau Contexte
        </v-btn>
      </v-col>
    </v-row>

    <!-- List -->
    <v-card rounded="xl" elevation="2">
      <v-data-table :items="contexts" :loading="loading" item-key="id" hover class="diagnostic-table">
        <!-- Product Make & Type -->
        <template #item.product="{ item }">
          <div class="clickable-row" @click="goToEditor(item)">
            <div class="font-weight-medium">
              {{ item.productMake }} - {{ item.productType }}
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ item.errorCodes.length }} codes erreur
            </div>
          </div>
        </template>

        <!-- Error Codes -->
        <template #item.errorCodes="{ item }">
          <div class="error-codes-cell">
            <v-chip v-for="errorCode in item.errorCodes" :key="errorCode.id" size="small" color="warning" variant="tonal" class="mr-2">
              {{ errorCode.code }}: {{ errorCode.message }}
            </v-chip>
          </div>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <v-btn icon="mdi-delete" variant="text" color="error" @click.stop="askDelete(item)" />
        </template>

        <template #headers>
          <tr>
            <th>Produit</th>
            <th>Codes Erreur</th>
            <th class="text-right">Actions</th>
          </tr>
        </template>

        <template #item="{ item }">
          <tr class="table-row" @click="goToEditor(item)">
            <td>
              <div class="font-weight-medium">
                {{ item.productMake }} - {{ item.productType }}
              </div>
              <div class="text-caption text-medium-emphasis">
                {{ item.errorCodes.length }} codes erreur
              </div>
            </td>

            <td>
              <div class="error-codes-cell">
                <v-chip v-for="errorCode in item.errorCodes" :key="errorCode.id" size="small" color="warning" variant="tonal" class="mr-2">
                  {{ errorCode.code }}: {{ errorCode.message }}
                </v-chip>
              </div>
            </td>

            <td class="text-right">
              <v-btn icon="mdi-delete" variant="text" color="error" @click.stop="askDelete(item)" />
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <!-- Delete confirmation -->
    <v-dialog :scrim="true" v-model="deleteDialog" max-width="420">
      <v-card rounded="xl">
        <v-card-title class="text-h6">
          Suppression du contexte de diagnostic?
        </v-card-title>

        <v-card-text>
          Cette action est irréversible.
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog = false">
            Annuler
          </v-btn>
          <v-btn color="error" @click="confirmDelete">
            Supprimer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>


<script setup lang="ts">
import { useAppStore } from '@/store'
import { ProductDiagnosticContext } from '@/types'
import { deleteProductDiagnosticContext, fetchProductDiagnosticContexts } from '@/utils/product-diagnostic-context-api'
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'


const router = useRouter();
const appStore = useAppStore();

const contexts = ref<ProductDiagnosticContext[]>([]);
const loading = ref(false);
const deleteDialog = ref(false);
const contextToDelete = ref<ProductDiagnosticContext | null>(null);

  const totalContexts = computed(() => contexts.value.length);

async function fetchContexts() {
  loading.value = true;
  try {
    contexts.value = await fetchProductDiagnosticContexts();
  } finally {
    loading.value = false;
  }
}

function goToEditor(context: ProductDiagnosticContext) {
  router.push({
    name: 'ProductDiagnosticContextEditor',
    params: { id: context.id }
  })
}

function createContext() {
  router.push({
    name: 'ProductDiagnosticContextEditor',
    params: { id: 'new' }
  })
}

function askDelete(context: ProductDiagnosticContext) {
  contextToDelete.value = context;
  deleteDialog.value = true;
}

async function confirmDelete() {
  deleteDialog.value = false
  if (!contextToDelete.value) {
    return;
  }
  try {
    loading.value = true
    await deleteProductDiagnosticContext(contextToDelete.value.id!);
    appStore.sendSuccessNotice('Contexte de diagnostic supprimé.');
    contextToDelete.value = null;
    fetchContexts();
  } catch (e) {
    // Ignore because it's handled in axios service
  }
  finally {
    loading.value = false
  }
}


onMounted(fetchContexts)
</script>

<style scoped>
.table-row {
  cursor: pointer;
  transition: background 0.15s ease;
}

.table-row:hover {
  background: rgba(var(--v-theme-primary), 0.05);
}

.error-codes-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 600px;
}
</style>
